import { SxProps, Typography } from '@mui/material';
import React, { ElementType, useRef, useState } from 'react';
import I18n from '../../i18n/I18n';
import { Icons } from '../../icons/Icons';
import { C, H, V } from '../../Layout';
import { useStyling } from '../../Theme';
import { ManageButton } from './ManageButton';
import { ShareOptionDisplay } from './ShareOptionDisplay';
import { ShareOptionsMenu } from './ShareOptionsMenu';

export type ShareOption = {
    value: boolean;
    labelToken: string;
    descriptionToken: string;
    icon?: ElementType;
};

export const defaultOptions: ShareOption[] = [
    {
        value: true,
        labelToken: 'common.open',
        descriptionToken: 'common.share.scope.org',
        icon: Icons.Unlock
    },
    {
        value: false,
        labelToken: 'common.share.scope.restricted',
        descriptionToken: 'common.share.scope.onlyMe',
        icon: Icons.Lock
    }
];

type ShareInfoProps = {
    options: ShareOption[];
    shared: boolean;
    onSharedChange: (shared: boolean) => void;
    titleToken?: string;
    containerSx?: SxProps;
    popoverMinWidth?: number;
};

export function ShareInfo({
    titleToken = 'common.share',
    options,
    shared = false,
    onSharedChange,
    containerSx,
    popoverMinWidth = 360
}: ShareInfoProps) {
    const { theme, isMobile } = useStyling();
    const [open, setOpen] = useState(false);
    const [initialShared] = useState(() => shared);
    const anchorRef = useRef<HTMLDivElement>(null);
    const selectedOption = options.find((opt) => opt.value === shared);

    const isDisabled = initialShared === true;

    const handleClose = (event?: Event) => {
        if (event && anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
            return;
        }
        setOpen(false);
    };

    const handleMenuItemClick = (value: boolean) => {
        onSharedChange(value);
        setOpen(false);
    };

    return (
        <V gap={1.5} sx={containerSx}>
            {titleToken && (
                <Typography
                    sx={{
                        fontWeight: theme.typography.fontWeightRegular,
                        color: theme.palette.primary.main,
                        textTransform: 'uppercase',
                        fontSize: '14px'
                    }}
                >
                    <I18n token={titleToken} />
                </Typography>
            )}
            <H
                gap={1}
                sx={{
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    flexDirection: isMobile ? 'column' : 'row'
                }}
            >
                <C
                    ref={anchorRef}
                    onClick={() => {
                        if (!isDisabled) {
                            setOpen((prevOpen) => !prevOpen);
                        }
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        flex: 1,
                        gap: 1,
                        cursor: isDisabled ? 'default' : 'pointer',
                        opacity: isDisabled ? 0.8 : 1,
                        pointerEvents: isDisabled ? 'none' : 'auto'
                    }}
                >
                    {selectedOption && <ShareOptionDisplay option={selectedOption} showDropdown />}
                </C>
                <ManageButton token="actions.manage" disabled={true} isMobile={isMobile} />
            </H>
            <ShareOptionsMenu
                open={open}
                anchorEl={anchorRef.current}
                options={options}
                selectedValue={shared}
                isMobile={isMobile}
                popoverMinWidth={popoverMinWidth}
                onClose={handleClose}
                onOptionClick={handleMenuItemClick}
            />
        </V>
    );
}
